import { useEffect, useRef } from "react";

export interface UseNutrientAnnotationEventsOptions {
	initialAnnotations?: InstantJSON;
	onAnnotationsChange?: (instantJson: InstantJSON) => void;
	ctxOnAnnotationsChange?: (instantJson: InstantJSON) => void;
	onError?: (error: string) => void;
	ctxOnError?: (error: string) => void;
}

/**
 * Registers Nutrient viewer event listeners and handles diffing to emit page-scoped annotation updates.
 */
export function useNutrientAnnotationEvents(
	instance: NutrientViewerInstance | null,
	{
		initialAnnotations,
		onAnnotationsChange,
		ctxOnAnnotationsChange,
		onError,
		ctxOnError,
	}: UseNutrientAnnotationEventsOptions,
): void {
	const isFirstRenderRef = useRef(true);
	const previousByIdRef = useRef<
		Map<string, { serialized: string; pageIndex: number }>
	>(new Map());

	// Seed previous snapshot cache from initial annotations to enable accurate diffing
	useEffect(() => {
		if (initialAnnotations?.annotations?.length) {
			const map = previousByIdRef.current;
			map.clear();
			for (const a of initialAnnotations.annotations) {
				map.set(a.id, {
					serialized: JSON.stringify(a),
					pageIndex: a.pageIndex,
				});
			}
		}
	}, [initialAnnotations]);

	useEffect(() => {
		if (!instance) return;

		// Disable annotation clicking/selection
		instance.addEventListener(
			"annotations.press",
			(event: { preventDefault: () => void }) => {
				event.preventDefault();
			},
		);

		// Emit page-scoped updates on change
		instance.addEventListener("annotations.change", (data: unknown) => {
			if (isFirstRenderRef.current) {
				isFirstRenderRef.current = false;
				return;
			}

			const handler = ctxOnAnnotationsChange ?? onAnnotationsChange;
			if (!handler) return;

			// Wait a tick to ensure export reflects latest state
			setTimeout(async () => {
				try {
					const instantJson = await instance.exportInstantJSON();
					let payload: InstantJSON = instantJson;

					// Try to discover the modified page index from event or by diffing
					const event =
						(data as { annotations?: InstantJSONAnnotation[] }) || undefined;
					const eventAnnotations = event?.annotations ?? [];
					let modifiedPageIndex: number | null = null;
					const lastChanged = eventAnnotations[eventAnnotations.length - 1];
					if (lastChanged && typeof lastChanged.pageIndex === "number") {
						modifiedPageIndex = lastChanged.pageIndex;
					}

					if (modifiedPageIndex === null) {
						const prev = previousByIdRef.current;
						const current = new Map<
							string,
							{ serialized: string; pageIndex: number }
						>();
						for (const a of instantJson.annotations ?? []) {
							const serialized = JSON.stringify(a);
							current.set(a.id, { serialized, pageIndex: a.pageIndex });
							const prevEntry = prev.get(a.id);
							if (!prevEntry || prevEntry.serialized !== serialized) {
								modifiedPageIndex = a.pageIndex;
							}
						}
						// Check deletions: ids present before but missing now
						if (modifiedPageIndex === null) {
							for (const [id, prevEntry] of prev.entries()) {
								if (!current.has(id)) {
									modifiedPageIndex = prevEntry.pageIndex;
									break;
								}
							}
						}
					}

					// Update previous snapshot cache
					{
						const snapshot = new Map<
							string,
							{ serialized: string; pageIndex: number }
						>();
						for (const a of instantJson.annotations ?? []) {
							snapshot.set(a.id, {
								serialized: JSON.stringify(a),
								pageIndex: a.pageIndex,
							});
						}
						previousByIdRef.current = snapshot;
					}

					// If no modifications detected, skip emitting (e.g., tool toggles)
					if (modifiedPageIndex === null) {
						return;
					}

					// Emit only annotations for the modified page
					{
						const all = instantJson.annotations ?? [];
						payload = {
							...instantJson,
							annotations: all.filter((a) => a.pageIndex === modifiedPageIndex),
						};
					}

					// Attach private page index for deletion cases (no annotations in payload)
					(payload as { __modifiedPageIndex?: number }).__modifiedPageIndex =
						modifiedPageIndex ?? undefined;

					await Promise.resolve(handler(payload));
				} catch (err) {
					const message =
						err instanceof Error
							? err.message
							: "Fehler beim Speichern der Annotationen";
					// Surface via context error first, then component onError
					try {
						ctxOnError?.(message);
					} catch {}
					onError?.(message);
				}
			}, 0);
		});

		// No explicit cleanup API is available on the instance; rely on container unload
		return;
	}, [
		instance,
		ctxOnAnnotationsChange,
		onAnnotationsChange,
		ctxOnError,
		onError,
	]);
}
